import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle,
  HelpCircle,
} from "@untitledui/icons"

import { cn } from "@/lib/utils"

import type { SectionLevel } from "../status"
import type { CheckState } from "../types"

/**
 * Status is carried by glyph shape + text, with colour as reinforcement — a
 * verdict must survive monochrome and colour-blindness. Icons are decorative
 * to assistive tech; the sr-only text is the accessible statement.
 */
const GLYPHS: Record<
  SectionLevel,
  { icon: typeof CheckCircle; className: string; srText: string }
> = {
  ok: { icon: CheckCircle, className: "text-fg-success-primary", srText: "OK" },
  attention: {
    icon: AlertCircle,
    className: "text-fg-warning-primary",
    srText: "Needs attention",
  },
  fail: {
    icon: AlertCircle,
    className: "text-fg-error-primary",
    srText: "Failing",
  },
  unknown: {
    icon: HelpCircle,
    className: "text-fg-quaternary",
    srText: "Cannot verify",
  },
}

export function StatusGlyph({
  level,
  className,
}: {
  level: SectionLevel
  className?: string
}) {
  const { icon: Icon, className: tone, srText } = GLYPHS[level]
  return (
    <span className={cn("inline-flex", className)}>
      <Icon aria-hidden className={cn("size-4 shrink-0", tone)} />
      <span className="sr-only">{srText}</span>
    </span>
  )
}

/**
 * Tri-state, because "we cannot tell" has to look different from "this is
 * broken". A `null` check renders neutral with a question mark, never in the
 * failure colour.
 */
export const checkLevel = (state: CheckState): SectionLevel =>
  state === true ? "ok" : state === false ? "fail" : "unknown"

export function CheckRow({
  state,
  label,
  detail,
}: {
  state: CheckState
  label: string
  detail: string
}) {
  return (
    <li className="flex items-start gap-2.5">
      <StatusGlyph level={checkLevel(state)} className="mt-0.5" />
      <div className="flex flex-col gap-0.5">
        <span className="text-secondary text-sm font-medium">{label}</span>
        <span className="text-tertiary text-sm">{detail}</span>
      </div>
    </li>
  )
}

export function Metric({
  label,
  value,
  tone,
}: {
  label: string
  value: number | string
  tone?: "neutral" | "warning" | "error"
}) {
  return (
    <div className="flex flex-col">
      <span
        className={cn(
          "text-primary text-display-xs font-semibold",
          tone === "warning" && "text-fg-warning-primary",
          tone === "error" && "text-fg-error-primary",
        )}
      >
        {value}
      </span>
      <span className="text-tertiary text-xs">{label}</span>
    </div>
  )
}

export function ExternalChip({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="border-secondary text-secondary hover:bg-secondary_subtle outline-focus-ring inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      {children}
      <ArrowUpRight aria-hidden className="size-3.5" />
      <span className="sr-only">(opens in new tab)</span>
    </a>
  )
}
